/**
 * ContentApprovalModule 데이터 형태 → 결재 목록 행
 * contentApprovalGroups / 결재프로세스 그룹 공통
 */

import {isAdmin} from "../../../../components/commonmethod/CommonMethod";
import {ContentCategory, ContentStatus} from "../../../../constant/CodeSpecNum";

function toUserId(value) {
    if (value == null || value === "") return null;
    return String(value);
}

function isApproverRecord(approver) {
    return approver != null && typeof approver === "object" && !Array.isArray(approver);
}

function resolveApproverRecipientId(approver) {
    if (!isApproverRecord(approver)) return null;
    return toUserId(approver.approverId ?? approver.id);
}

/**
 * 레거시 contentApprovers에는 proxy가 없음.
 * 필드가 없거나 null이면 null을 반환하고, recipient(approverId) 매칭만 사용한다.
 */
function resolveApproverProxyId(approver) {
    if (!isApproverRecord(approver)) return null;

    const proxy = approver.proxy;
    if (isApproverRecord(proxy) || (proxy != null && typeof proxy === "object")) {
        const proxyId = proxy.id ?? proxy.userId ?? proxy.approverId;
        if (proxyId != null) {
            return toUserId(proxyId);
        }
    }

    if (approver.proxyId != null) {
        return toUserId(approver.proxyId);
    }

    return null;
}

/** 실제 결재자가 수신자의 대결자(proxy)인지 — 레거시(proxy 없음)는 항상 false */
export function isProxyApprovalAction(item) {
    if (item?.approvedUserId == null) {
        return false;
    }

    const recipientId = toUserId(item.actor?.id);
    const approverId = toUserId(item.approvedUserId);
    if (!recipientId || !approverId || recipientId === approverId) {
        return false;
    }

    const proxyId = resolveApproverProxyId(item);
    return proxyId != null && proxyId === approverId;
}

/** 현재 결재 대기 그룹의 수신자 목록 */
export function getActiveGroupApprovers(contentApproval, approvableGroup, fallbackApprovers = []) {
    const safeFallback = Array.isArray(fallbackApprovers) ? fallbackApprovers : [];
    const groupId = approvableGroup?.id;

    if (groupId == null) {
        return safeFallback;
    }

    const groups = contentApproval?.contentApprovalGroups;
    const activeGroup = Array.isArray(groups)
        ? groups.find((group) => group?.id === groupId)
        : null;

    const approvers = activeGroup?.contentApprovers
        ?? approvableGroup?.contentApprovers
        ?? safeFallback;

    return Array.isArray(approvers) ? approvers : [];
}

/** 결재 처리 권한: 현재 단계 수신자(결재자) · 수신자 대결자(있을 때만) · 관리자 */
export function canUserGrantApproval(approvers, user) {
    const userId = toUserId(user?.id);
    if (!userId) return false;

    if (isAdmin(user)) {
        return true;
    }

    const list = Array.isArray(approvers) ? approvers : [];
    if (list.length === 0) {
        return false;
    }

    return list.some((approver) => {
        if (!isApproverRecord(approver)) {
            return false;
        }

        const recipientId = resolveApproverRecipientId(approver);
        if (recipientId === userId) {
            return true;
        }

        const proxyId = resolveApproverProxyId(approver);
        return proxyId != null && proxyId === userId;
    });
}

export function mapContentApprovalGroups(groups) {
    if (!Array.isArray(groups)) return [];
    return groups.map((group) => ({
        ...group,
        name: group.name ?? group.approvalGroupName ?? "",
        approvers: group.approvers ?? group.contentApprovers ?? [],
    }));
}

export function groupsToApprovalRows(groups) {
    const rows = [];

    mapContentApprovalGroups(groups).forEach((group, groupIndex) => {
        const order = Number.isFinite(group.orders) ? group.orders : groupIndex;
        const approvers = Array.isArray(group.approvers) ? group.approvers : [];
        const groupId = group.id;
        const groupName = group.name ?? group.approvalGroupName ?? "";
        const shared = {
            groupName,
            groupId,
            approvalType: group.approvalType,
            approvedUserId: group.approvedUserId,
            approvedUserName: group.approvedUserName,
            updatedAt: group.updatedAt,
            description: group.description ?? "",
        };

        if (approvers.length === 0) {
            rows.push({
                rowKey: `group-${groupId ?? groupIndex}-passed`,
                orders: order,
                actor: {id: `passed-${groupId ?? groupIndex}`, name: "전결"},
                department: "",
                isPassed: true,
                isLegacyMulti: false,
                legacyIndex: 0,
                ...shared,
            });
            return;
        }

        approvers.forEach((approver, approverIndex) => {
            const actorId = approver.approverId ?? approver.id ?? `${groupIndex}-${approverIndex}`;
            rows.push({
                rowKey: `group-${groupId ?? groupIndex}-approver-${actorId}`,
                orders: order,
                actor: {
                    id: actorId,
                    name: approver.name ?? approver.approvedUserName ?? "결재자",
                },
                department: approver.department?.name ?? approver.department ?? "",
                proxy: approver.proxy ?? null,
                proxyId: approver.proxyId ?? null,
                isPassed: false,
                isLegacyMulti: approvers.length > 1,
                legacyIndex: approverIndex,
                ...shared,
            });
        });
    });

    return rows.sort((a, b) => a.orders - b.orders);
}

/** 평탄 행 목록 → 그룹 단위 클러스터 */
export function clusterApprovalRows(rows) {
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) {
        return [];
    }

    if (list[0]?.members) {
        return list;
    }

    const sorted = [...list].sort((a, b) => a.orders - b.orders);
    const clusters = [];
    let index = 0;

    while (index < sorted.length) {
        const row = sorted[index];

        if (row.isLegacyMulti && row.groupId != null) {
            const members = [];
            const groupId = row.groupId;

            while (
                index < sorted.length
                && sorted[index].groupId === groupId
                && sorted[index].isLegacyMulti
            ) {
                members.push(sorted[index]);
                index += 1;
            }

            clusters.push({
                clusterKey: `cluster-${groupId}`,
                orders: members[0].orders,
                groupId,
                groupName: members[0].groupName ?? "",
                isGroupBundle: members.length > 1,
                memberCount: members.length,
                approvalType: members[0].approvalType,
                approvedUserId: members[0].approvedUserId,
                approvedUserName: members[0].approvedUserName,
                updatedAt: members[0].updatedAt,
                description: members[0].description ?? "",
                members,
            });
            continue;
        }

        clusters.push({
            clusterKey: row.rowKey,
            orders: row.orders,
            groupId: row.groupId ?? null,
            groupName: row.groupName ?? "",
            isGroupBundle: false,
            memberCount: 1,
            approvalType: row.approvalType,
            approvedUserId: row.approvedUserId,
            approvedUserName: row.approvedUserName,
            updatedAt: row.updatedAt,
            description: row.description ?? "",
            members: [row],
        });
        index += 1;
    }

    return clusters;
}

export function countApprovalMembers(clusters) {
    return (clusters ?? []).reduce((sum, cluster) => sum + (cluster.members?.length ?? 0), 0);
}

export function contentApprovalToRows(contentApproval) {
    return groupsToApprovalRows(contentApproval?.contentApprovalGroups ?? []);
}

/** 프리셋 models → 모달 결재자 배열 */
export function presetModelsToApprovers(models) {
    return (models ?? []).map((model, index) => {
        const next = {...model};
        if ("id" in next) delete next.id;
        return {
            ...next,
            orders: Number.isFinite(next.orders) ? next.orders : index,
        };
    });
}

/** ApprovalProcessResponseDto → 모달 결재자 배열 (레거시: 그룹 순서 유지, 그룹 내 다수 결재자 평탄화) */
export function approvalProcessToApprovers(process) {
    if (!process) return [];

    if (Array.isArray(process.approvers) && process.approvers.length > 0) {
        return process.approvers.map((user, index) => ({
            orders: index,
            actor: {
                id: user.id,
                name: user.name ?? "결재자",
                department: user.department,
                profileImage: user.profileImage,
            },
        }));
    }

    const groups = mapContentApprovalGroups(process.approvalGroups ?? [])
        .sort((a, b) => (a.orders ?? 0) - (b.orders ?? 0));

    const result = [];
    let order = 0;

    groups.forEach((group) => {
        const groupApprovers = Array.isArray(group.approvers) ? group.approvers : [];
        const isLegacyMulti = groupApprovers.length > 1;

        groupApprovers.forEach((approver, approverIndex) => {
            const actorId = approver.approverId ?? approver.id ?? approver.userId;
            if (actorId == null) return;

            result.push({
                orders: order,
                groupId: group.id,
                groupName: group.name ?? group.approvalGroupName ?? "",
                isLegacyMulti,
                legacyIndex: approverIndex,
                actor: {
                    id: actorId,
                    name: approver.name ?? approver.approvedUserName ?? approver.userName ?? "결재자",
                    department: approver.department,
                    profileImage: approver.profileImage,
                },
            });
            order += 1;
        });
    });

    return result;
}

/** gov_front Approve[] → 목록 행 */
export function approversToRows(approvers) {
    return (approvers ?? [])
        .map((item, index) => ({
            rowKey: `pick-${item.orders ?? index}-${item.actor?.id ?? index}`,
            orders: Number.isFinite(item.orders) ? item.orders : index,
            actor: {
                id: item.actor?.id ?? index,
                name: item.actor?.name ?? "결재자",
                profileImage: item.actor?.profileImage,
            },
            department: item.actor?.department?.name ?? item.actor?.department ?? "",
            groupName: item.groupName ?? "",
            groupId: item.groupId ?? null,
            isLegacyMulti: Boolean(item.isLegacyMulti),
            legacyIndex: item.legacyIndex ?? 0,
            isPassed: false,
        }))
        .sort((a, b) => a.orders - b.orders);
}

/** 모달 결재선 → SetApprovesRequestDto */
export function approversToSetApprovesRequest(approvers) {
    const sorted = [...(approvers ?? [])].sort((a, b) => (a.orders ?? 0) - (b.orders ?? 0));

    return {
        users: sorted
            .map((item) => {
                const id = item.actor?.id ?? item.id ?? item.approverId;
                if (id == null) {
                    return null;
                }
                return {id: Number(id)};
            })
            .filter(Boolean),
    };
}

/** setApproves / 결재프로세스 조회 응답을 approvalProcess 형태로 통일 */
export function normalizeApprovalProcess(approvalProcess) {
    if (!approvalProcess || typeof approvalProcess !== "object") {
        return null;
    }

    const id = approvalProcess.id ?? approvalProcess.approvalProcessId;
    if (id == null) {
        return null;
    }

    return {
        ...approvalProcess,
        id: Number(id),
    };
}

/** 환경설정 결재라인 저장 요청 */
export function buildApprovalProcessSetRequest({approvalProcessId, name, description, approvers}) {
    return {
        ...approversToSetApprovesRequest(approvers),
        ...(approvalProcessId != null ? {approvalProcessId: Number(approvalProcessId)} : {}),
        ...(name?.trim() ? {name: name.trim()} : {}),
        ...(description?.trim() ? {description: description.trim()} : {}),
    };
}

/** 모달 결재선 → ApprovalPresetSaveRequestDto */
export function approversToPresetSaveRequest({name, description, approvers}) {
    const {users} = approversToSetApprovesRequest(approvers);

    return {
        name: name.trim(),
        ...(description?.trim() ? {description: description.trim()} : {}),
        users,
    };
}

/** 결재선 등록 UI를 노출할 수 있는 문건 진행 단계인지 */
export function isApprovalRegistrationStage(contentStatus, contentCategory, approvalSeparator = false) {
    const current = contentStatus?.statusNumericValue;
    if (current == null || Number.isNaN(Number(current))) {
        return false;
    }

    if (approvalSeparator) {
        return current === ContentStatus.POST_APPROVE.statusNumericValue;
    }

    switch (contentCategory) {
        case ContentCategory.SERVICE_APPLY:
            return current === ContentStatus.APPLY_APPROVE.statusNumericValue;
        case ContentCategory.SERVICE_REQUEST:
            return current === ContentStatus.PRE_REGISTER.statusNumericValue
                || current === ContentStatus.APPROVE.statusNumericValue;
        default:
            return current === ContentStatus.APPROVE.statusNumericValue;
    }
}

/**
 * ContentApprovalModule 상세와 동일 — 결재 데이터 없을 때 진행 단계가 맞지 않으면 숨김
 */
export function shouldShowApprovalComponent({
    contentId,
    hasContentApproval,
    contentStatus,
    contentCategory,
    approvalSeparator = false,
}) {
    if (!contentId) {
        return true;
    }

    if (hasContentApproval) {
        return true;
    }

    return isApprovalRegistrationStage(contentStatus, contentCategory, approvalSeparator);
}
